import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Calendar } from 'lucide-react';
import { formatTimeFromISO } from '@/utils/timezoneUtils';
import { useLanguage } from '@/i18n/LanguageContext';

function StaffReportsPage() {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [summary, setSummary] = useState({
    timezone: 'UTC',
    total_days: 0,
    present_days: 0,
    late_days: 0,
    absent_days: 0,
    total_break_minutes: 0,
    records: []
  });
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchReportsSummary();
  }, []);
  
  const fetchReportsSummary = async () => {
    try {
      const response = await axios.get(`${API}/staff/reports/summary?days=30`, {
        withCredentials: true
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch reports summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    return formatTimeFromISO(timeStr);
  };

  const calculateBreakSummary = (breaks) => {
    if (!breaks || breaks.length === 0) return '0 breaks (0 min)';
    const totalMinutes = breaks.reduce((sum, b) => sum + (b.duration || 0), 0);
    return `${breaks.length} breaks (${totalMinutes} min)`;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reports-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-8 p-6" data-testid="staff-reports-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900" data-testid="reports-title">
          Attendance History
        </h1>
        <p className="text-gray-500 mt-1">Your attendance records for the last 30 days</p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Days */}
        <Card className="border border-gray-200 rounded-xl shadow-sm" data-testid="total-days-card">
          <CardContent className="p-6 text-center">
            <p className="text-4xl font-bold text-purple-600" data-testid="total-days-value">
              {summary.total_days}
            </p>
            <p className="text-gray-500 mt-2 text-sm">Total Days</p>
          </CardContent>
        </Card>

        {/* Present Days */}
        <Card className="border border-gray-200 rounded-xl shadow-sm" data-testid="present-days-card">
          <CardContent className="p-6 text-center">
            <p className="text-4xl font-bold text-green-500" data-testid="present-days-value">
              {summary.present_days}
            </p>
            <p className="text-gray-500 mt-2 text-sm">Present Days</p>
          </CardContent>
        </Card>

        {/* Late Days */}
        <Card className="border border-gray-200 rounded-xl shadow-sm" data-testid="late-days-card">
          <CardContent className="p-6 text-center">
            <p className="text-4xl font-bold italic text-orange-500" data-testid="late-days-value">
              {summary.late_days}
            </p>
            <p className="text-gray-500 mt-2 text-sm">Late Days</p>
          </CardContent>
        </Card>

        {/* Total Break */}
        <Card className="border border-gray-200 rounded-xl shadow-sm" data-testid="total-break-card">
          <CardContent className="p-6 text-center">
            <p className="text-4xl font-bold italic text-orange-500" data-testid="total-break-value">
              {summary.total_break_minutes}
            </p>
            <p className="text-gray-500 mt-2 text-sm">Total Break (min)</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Recent Records Table */}
      <Card className="border border-gray-200 rounded-xl shadow-sm" data-testid="recent-records-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Calendar className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">Recent Records</h2>
          </div>
          
          {summary.records.length === 0 ? (
            <div className="text-center py-8 text-gray-500" data-testid="no-records-message">
              No attendance records found for the last 30 days
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-200">
                    <TableHead className="text-gray-500 font-medium">{t("common.date")}</TableHead>
                    <TableHead className="text-gray-500 font-medium">{t("attendance.check_in")}</TableHead>
                    <TableHead className="text-gray-500 font-medium">{t("attendance.check_out")}</TableHead>
                    <TableHead className="text-gray-500 font-medium">{t("common.status")}</TableHead>
                    <TableHead className="text-gray-500 font-medium">Late By</TableHead>
                    <TableHead className="text-gray-500 font-medium">Breaks</TableHead>
                    <TableHead className="text-gray-500 font-medium">Total Working</TableHead>
                    <TableHead className="text-gray-500 font-medium">Overtime</TableHead>
                    <TableHead className="text-gray-500 font-medium">Sessions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.records.map((record, index) => (
                    <TableRow 
                      key={record.attendance_id || index} 
                      className="border-b border-gray-100"
                      data-testid={`attendance-record-${index}`}
                    >
                      <TableCell className="font-medium text-gray-900">
                        {formatDate(record.date)}
                      </TableCell>
                      <TableCell className="text-gray-700">
                        {formatTime(record.check_in)}
                      </TableCell>
                      <TableCell className="text-gray-700">
                        {formatTime(record.check_out)}
                      </TableCell>
                      <TableCell>
                        <span 
                          className={`
                            px-3 py-1 rounded-md text-xs font-medium capitalize
                            ${record.status === 'present' ? 'bg-green-100 text-green-700' : ''}
                            ${record.status === 'late' ? 'bg-amber-100 text-amber-700' : ''}
                            ${record.status === 'absent' ? 'bg-red-100 text-red-700' : ''}
                            ${record.status === 'not_marked' ? 'bg-gray-100 text-gray-700' : ''}
                          `}
                          data-testid={`status-badge-${index}`}
                        >
                          {record.status === 'present' ? 'Present' : 
                           record.status === 'late' ? 'Late' : 
                           record.status === 'absent' ? 'Absent' : 
                           record.status === 'not_marked' ? 'Not Marked' :
                           record.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {record.late_by > 0 ? (
                          <span className="text-orange-600 font-medium" data-testid={`late-by-${index}`}>
                            {record.late_by} min
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {calculateBreakSummary(record.breaks)}
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {record.total_working_minutes > 0 ? (
                          <span className="font-medium text-green-600">
                            {Math.floor(record.total_working_minutes / 60)}h {Math.round(record.total_working_minutes % 60)}m
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {record.overtime_minutes > 0 ? (
                          <span className="font-semibold text-purple-600">
                            {Math.floor(record.overtime_minutes / 60)}h {Math.round(record.overtime_minutes % 60)}m
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {record.sessions_count > 1 ? (
                          <button
                            onClick={() => navigate(`/dashboard/reports/sessions/${record.date}`)}
                            className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium hover:bg-blue-200 transition-colors cursor-pointer"
                            data-testid={`view-sessions-${record.date}`}
                          >
                            {record.sessions_count} sessions →
                          </button>
                        ) : (
                          <span className="text-gray-400">1 session</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default StaffReportsPage;
